import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";

import { leaveHistoryShowPath } from "@/containers/staff/leave-requests/leave-request-helpers";
import { formatListDate, formatWeekdayFull } from "@/utils/date-format";

import {
  canOpenLeaveShow,
  leaveStatusChipColor,
  leaveTypeLabel,
  type LeaveDayDrilldown,
  type OfficeLeaveCalendarEntry,
} from "../leave-calendar-helpers";

type Props = {
  open: boolean;
  drilldown: LeaveDayDrilldown | null;
  month: string;
  onClose: () => void;
  canOfficeView: boolean;
  linkedEmployeeId: number | null;
};

export function LeaveDayDrawer({
  open,
  drilldown,
  month,
  onClose,
  canOfficeView,
  linkedEmployeeId,
}: Props) {
  const day = drilldown?.day;
  const date = day != null ? dayjs(`${month}-${String(day).padStart(2, "0")}`) : null;
  const entries = drilldown?.entries ?? [];
  const employeeName = drilldown?.employee.name;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 360 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" component="h2">
            Leave detail
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>

        {employeeName ? (
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            {employeeName}
          </Typography>
        ) : null}

        {date ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatWeekdayFull(date)} · {formatListDate(date)}
          </Typography>
        ) : null}

        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No pending or approved leave.
          </Typography>
        ) : (
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {entries.map((entry) => (
              <LeaveDayEntry
                key={entry.id}
                entry={entry}
                canOfficeView={canOfficeView}
                linkedEmployeeId={linkedEmployeeId}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function LeaveDayEntry({
  entry,
  canOfficeView,
  linkedEmployeeId,
}: {
  entry: OfficeLeaveCalendarEntry;
  canOfficeView: boolean;
  linkedEmployeeId: number | null;
}) {
  const showLink = canOpenLeaveShow(entry, { canOfficeView, linkedEmployeeId });
  const showPath = leaveHistoryShowPath(canOfficeView ? "office" : "self", entry.id);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {leaveTypeLabel(entry.leaveType)}
        </Typography>
        <Chip label={entry.status} size="small" color={leaveStatusChipColor(entry.status)} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {formatListDate(entry.startDate)}
        {entry.startDate !== entry.endDate ? ` – ${formatListDate(entry.endDate)}` : ""}
      </Typography>
      {showLink ? (
        <MuiLink component={RouterLink} to={showPath} underline="hover" variant="body2">
          View request
        </MuiLink>
      ) : null}
    </Box>
  );
}
